import { z } from 'zod';

/** Auth0 roles, ordered from least to most privileged. */
export const ROLES = ['ops-viewer', 'ops-operator', 'ops-approver', 'ops-admin'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const PERMISSIONS = [
  'submissions:read',
  'submissions:start',
  'submissions:cancel',
  'submissions:retry',
  'decisions:submit',
  'notes:write',
  'audit:read',
] as const;
export const permissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof permissionSchema>;

/**
 * Role to permission map. Roles form a superset chain:
 * viewer < operator < approver < admin. Each role includes everything the previous one has.
 *
 * - ops-viewer: read submissions
 * - ops-operator: + start and cancel submissions, write notes
 * - ops-approver: + submit decisions
 * - ops-admin: + everything else (audit log, retrying failed autopilot runs)
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  'ops-viewer': ['submissions:read'],
  'ops-operator': ['submissions:read', 'submissions:start', 'submissions:cancel', 'notes:write'],
  'ops-approver': [
    'submissions:read',
    'submissions:start',
    'submissions:cancel',
    'notes:write',
    'decisions:submit',
  ],
  'ops-admin': [...PERMISSIONS],
};

const isRole = (value: string): value is Role => (ROLES as readonly string[]).includes(value);

/** Union of permissions for the given roles. Unknown roles are ignored. Order follows PERMISSIONS. */
export function permissionsForRoles(roles: string[]): Permission[] {
  const granted = new Set<Permission>();
  for (const role of roles) {
    if (isRole(role)) for (const p of ROLE_PERMISSIONS[role]) granted.add(p);
  }
  return PERMISSIONS.filter((p) => granted.has(p));
}

export function hasPermission(perms: readonly Permission[], p: Permission): boolean {
  return perms.includes(p);
}
